import { pantryColors } from '@pantry/ui'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { LoyaltyCouponLocationOption } from '../context/LoyaltyCouponsContext'

const p = pantryColors

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[800],
  background: p.white,
  outline: 'none',
}

const linkButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontSize: 12,
  fontWeight: 600,
  color: p.brand.primary,
  cursor: 'pointer',
}

interface Props {
  options: LoyaltyCouponLocationOption[]
  selectedIds: number[]
  onChange: (next: number[]) => void
}

/**
 * Search-select for the Specific mode, styled like `CouponModeCategories`: a
 * search input, a checkbox dropdown filtered by name, and removable chips for
 * the current selection. Opens (and focuses the input) on mount when nothing
 * is selected yet, so switching to Specific lands ready to pick.
 */
export const CouponLocationsSelect: React.FC<Props> = ({
  options,
  selectedIds,
  onChange,
}) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(() => selectedIds.length === 0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus imperatively (not `autoFocus`) — only for the empty, freshly
  // switched-to state; a mount-time check by design.
  useEffect(() => {
    if (selectedIds.length === 0) inputRef.current?.focus()
  }, [])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  // Chips follow the options' (name-sorted) order rather than click order.
  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(o.id)),
    [options, selectedSet],
  )
  const results = useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    return trimmed
      ? options.filter((o) => o.name.toLowerCase().includes(trimmed))
      : options
  }, [options, query])

  // Close the dropdown on outside click or Esc.
  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const toggle = (id: number) => {
    onChange(
      selectedSet.has(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          ref={wrapRef}
          style={{ position: 'relative', flex: 1, minWidth: 0 }}
        >
          <input
            ref={inputRef}
            style={inputStyle}
            type="text"
            placeholder="Search locations…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            autoComplete="off"
          />
          {open && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                maxHeight: 220,
                overflowY: 'auto',
                background: p.white,
                border: `1px solid ${p.gray[200]}`,
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                zIndex: 2,
              }}
            >
              {results.length === 0 ? (
                <div
                  style={{
                    padding: '10px 12px',
                    fontSize: 12,
                    color: p.gray[500],
                  }}
                >
                  No locations match "{query}"
                </div>
              ) : (
                results.map((o) => (
                  <label
                    key={o.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      fontSize: 13,
                      color: p.gray[800],
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = p.gray[50]
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSet.has(o.id)}
                      onChange={() => toggle(o.id)}
                      style={{ margin: 0, accentColor: p.brand.primary }}
                    />
                    {o.name}
                  </label>
                ))
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          style={{ ...linkButtonStyle, flexShrink: 0 }}
          onClick={() => onChange([])}
        >
          Clear
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          marginTop: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            flex: 1,
            minWidth: 0,
          }}
        >
          {selectedOptions.map((o) => (
            <span
              key={o.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                background: p.brand.light,
                color: p.brand.primary,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {o.name}
              <button
                type="button"
                onClick={() => toggle(o.id)}
                aria-label={`Remove ${o.name}`}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: p.brand.primary,
                  fontSize: 14,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <span
          style={{
            flexShrink: 0,
            whiteSpace: 'nowrap',
            lineHeight: '24px',
            fontSize: 12,
            color: p.gray[500],
          }}
        >
          {selectedOptions.length} of {options.length} selected
        </span>
      </div>
    </div>
  )
}
